import React, { useState, useEffect } from 'react'
import { fetchQuestionsWithPolling, selectSkillName, selectSubskills } from '../../../redux/skillSlice'
import { useDispatch, useSelector } from 'react-redux'
import { Check } from 'lucide-react'
import { selectInitSessionUserDetails } from '../../../redux/onboardingUserDetails'
import { useNavigate } from 'react-router-dom'
import { Mixpanel } from '../../../config/Mixpanel'
import constants from '../../../utilities/constants'

const LoaderTwo = () => {
  const [scanPosition, setScanPosition] = useState(0)
  const [scanDirection, setScanDirection] = useState(1) // 1 for down, -1 for up
  const [statusText, setStatusText] = useState('Understanding your current level')

  const subskills = useSelector(selectSubskills)
  const dispatch = useDispatch()
  const userSessionDetails = useSelector(selectInitSessionUserDetails)
  const navigate = useNavigate()
  const skillName = useSelector(selectSkillName)

  // Scanning line animation - up and down continuously


  useEffect(() => {
    const interval = setInterval(() => {
      setScanPosition(prev => {
        const newPosition = prev + (scanDirection * 1.5)

        // If reached bottom, change direction to go up
        if (newPosition >= 95) {
          setScanDirection(-1)
          return 95
        }

        // If reached top, change direction to go down
        if (newPosition <= 5) {
          setScanDirection(1)
          return 5
        }

        return newPosition
      })
    }, 30)

    return () => clearInterval(interval)
  }, [scanDirection])

  useEffect(() => {
    // Track loader page load
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_LOADER_TWO_LOADED, {
      skill: skillName,
      session_id: userSessionDetails?.session_id
    })

    const t1 = setTimeout(() => {
      setStatusText('Adjusting Difficulty To Match Your Level')
    }, 2000)

    const t2 = setTimeout(() => {
      setStatusText('Selecting Relevant Questions')
    }, 4000)

    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [])


  useEffect(() => {

    const fetchQuestions = async () => {
      await dispatch(fetchQuestionsWithPolling({
        session_id: userSessionDetails?.session_id
      }))
      navigate('/onboarding/mcq/1', { replace: true })
    }

    if (userSessionDetails?.session_id) {
      fetchQuestions()
    }
  }, [dispatch, navigate, userSessionDetails?.session_id])

  return (

    <div className='flex flex-col h-full gap-6 justify-between items-center'>
      <style>{`
        @keyframes lt-pop {
          0% { opacity: 0; transform: scale(0.6) translateY(6px); }
          60% { opacity: 1; transform: scale(1.08) translateY(0px); }
          100% { opacity: 1; transform: scale(1) translateY(0px); }
        }
        .lt-pop {
          transform-origin: center;
          animation: lt-pop 520ms cubic-bezier(0.2, 0.85, 0.2, 1) both;
          will-change: transform, opacity;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      `}</style>
      <div className="w-full md:max-w-[45rem] flex-1 justify-center items-center mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col">

        {/* Main Content Container */}
        <div className="max-w-2xl mx-auto text-center relative z-10">

          {/* Circular Loader with Skills */}
          <div className="mb-12 relative flex items-center justify-center">
            <div className="relative">

              {/* Outer Circle - Dark Navy */}
              <div className="w-[200px] h-[200px] rounded-full bg-[#072649]  flex items-center justify-center">

                {/* Inner Circle - Blue */}
                <div className="w-40 h-40 rounded-full bg-[#406AFF]/30 border border-[#406AFF] shadow-inner relative flex items-center justify-center">

                  {/* Central Square with Scanning Effect */}
                  <div className="w-20 h-20 bg-[#07162F] rounded-[8px] relative overflow-hidden flex items-center justify-center shadow-lg">
                    {/* Refresh Icon */}
                    <img src="https://img.learntube.ai/public-assets/personalise/ri_search-ai-2-line.webp" className='w-[24px] h-[24px]' alt="" />

                    {/* Scanning Line */}
                    <div
                      className="absolute w-full h-0.5 z-10"
                      style={{
                        top: `${scanPosition}%`,
                        backgroundImage:
                          'linear-gradient(90deg, rgba(64, 106, 255, 0) 0%, rgba(64, 106, 255, 1) 57%, rgba(64, 106, 255, 0) 100%)',
                        opacity: 1,
                        filter: 'blur(0.5px)',
                        boxShadow: '0 0 10px rgba(64, 106, 255, 0.9), 0 0 20px rgba(64, 106, 255, 0.6)'
                      }}
                    ></div>

                    {/* Additional glow effect */}
                    <div
                      className="absolute w-full h-1 z-5"
                      style={{
                        top: `${scanPosition}%`,
                        backgroundImage:
                          'linear-gradient(90deg, rgba(64, 106, 255, 0) 0%, rgba(64, 106, 255, 0.3) 57%, rgba(64, 106, 255, 0) 100%)',
                        filter: 'blur(2px)'
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Floating Skill Labels */}
              <div className="absolute inset-0 w-[200px] h-[200px]">
                {/* Strategy - Top Right */}
                <div className="absolute" style={{
                  top: '15%',
                  right: '-15%',
                }}>
                  <div
                    className="lt-pop bg-[#406AFF] text-white px-4 py-2 rounded-full text-[8px] font-medium shadow-lg hover:scale-105 transition-transform w-[90px] flex items-center justify-center"
                    style={{ animationDelay: '1s' }}
                  >
                    <p className='text-center line-clamp-3 overflow-hidden leading-tight'>
                      {subskills[0]?.name}
                    </p>
                  </div>
                </div>

                {/* Product Thinking - Left */}
                <div className="absolute" style={{
                  top: '45%',
                  left: '-20%',
                }}>
                  <div
                    className="lt-pop bg-[#406AFF] text-white px-4 py-2 rounded-full text-[8px] font-medium shadow-lg hover:scale-105 transition-transform w-[90px] flex items-center justify-center"
                    style={{ animationDelay: '1.5s' }}
                  >
                    <p className='text-center line-clamp-3 overflow-hidden leading-tight'>
                      {subskills[1]?.name}
                    </p>
                  </div>
                </div>

                {/* User Research - Bottom Right */}
                <div className="absolute" style={{
                  bottom: '2%',
                  right: '-5%',
                }}>
                  <div
                    className="lt-pop bg-[#406AFF] text-white px-4 py-2 rounded-full text-[8px] font-medium shadow-lg hover:scale-105 transition-transform w-[90px] flex items-center justify-center"
                    style={{ animationDelay: '2s' }}
                  >
                    <p className='text-center line-clamp-3 overflow-hidden leading-tight'>
                      {subskills[2]?.name}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Title with Gradient Animation */}
          <div className="mt-8">
            <div className='flex flex-col'>
              <h1 className='capitalize text-center bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[130%] capitalize'>
                Building your {skillName} skill check...
              </h1>
            </div>
          </div>

          {/* Bottom Status */}
          <div className="mt-6 flex items-center justify-center py-3 px-3 rounded-[12px]" style={{
            backgroundImage: `linear-gradient(
    to bottom,
    rgba(64, 106, 255, 0.5) 0%,
    rgba(64, 106, 255, 0) 100%
    )` }}>
            <div className="w-[14px] h-[14px] bg-[#54B226] p-[2px]  rounded-full flex items-center justify-center animate-pulse">
              <Check className='w-full h-full text-white' />
            </div>
            <p className="text-[14px] text-white/50 font-normal ml-1 text-center">
              {statusText}
            </p>
          </div>

        </div>
      </div>
      {/* <div className='capitalize text-center text-[12px] text-[#83BEFF] font-bold mb-3'>🚀 TAKES ONLY 2 MINUTES</div> */}
    </div>
  )
}

export default LoaderTwo